import React, { useState, useEffect } from 'react';
import { Music, Play, Heart, ChevronRight, Headphones, Sparkles, Youtube, ExternalLink } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

// Sprint 207c — deep-link each track out to the teen's preferred streaming
// service. No API keys, no auth, no quotas. Opens a pre-searched results
// page so the teen can play the exact track in one click.
const buildStreamingLinks = (track) => {
  const q = encodeURIComponent(`${track.title} ${track.artist}`);
  return {
    spotify: `https://open.spotify.com/search/${q}`,
    youtube: `https://www.youtube.com/results?search_query=${q}`,
    apple: `https://music.apple.com/search?term=${q}`,
  };
};

const MoodPlaylist = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMood, setSelectedMood] = useState(null);
  const [specificPlaylist, setSpecificPlaylist] = useState(null);

  useEffect(() => {
    apiClient.request('/api/self-dev/mood-playlist')
      .then(d => { setData(d); setSelectedMood(d.current_mood); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSelectMood = async (mood) => {
    setSelectedMood(mood);
    if (mood === data?.current_mood) {
      setSpecificPlaylist(null);
      return;
    }
    try {
      const res = await apiClient.request(`/api/self-dev/mood-playlist/${mood}`);
      setSpecificPlaylist(res.playlist);
    } catch { /* noop — fall back to default playlist */ }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading your playlist...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="p-8 text-center text-gray-400">Could not load playlist</div></DashboardLayout>;

  const activePlaylist = specificPlaylist || data.playlist;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="mood-playlist-page">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center justify-center gap-2">
            <Headphones size={28} className="text-violet-600" />
            Mood Playlist
          </h1>
          <p className="text-sm text-gray-500 mt-1">Music matched to your emotional state</p>
          <p className="text-[11px] text-gray-400 mt-1">Tap <span className="inline-block w-3 h-3 rounded-full align-middle" style={{ background: '#1DB954' }} /> Spotify, <span className="inline-block w-3 h-3 rounded-full align-middle" style={{ background: '#FF0000' }} /> YouTube, or <span className="inline-block w-3 h-3 rounded-full align-middle" style={{ background: 'linear-gradient(135deg, #FA233B 0%, #FB5C74 100%)' }} /> Apple Music to listen — opens in a new tab.</p>
        </div>

        {/* Current Mood Banner */}
        <div className={`rounded-2xl p-5 sm:p-6 mb-6 bg-gradient-to-r ${activePlaylist.bg} text-white shadow-lg`} data-testid="mood-banner">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider opacity-75 mb-1">
                {data.has_mood_data ? 'Based on your mood' : 'Default playlist'}
              </p>
              <h2 className="text-xl sm:text-2xl font-black">{activePlaylist.mood_label}</h2>
              <p className="text-sm opacity-80 mt-1 max-w-md">{activePlaylist.description}</p>
            </div>
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Music size={24} className="text-white" />
            </div>
          </div>
        </div>

        {/* Mood Selector Pills */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="mood-selector">
          {data.all_playlists.map(p => (
            <button
              key={p.key}
              onClick={() => handleSelectMood(p.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                selectedMood === p.key
                  ? 'text-white shadow-md scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={selectedMood === p.key ? { backgroundColor: p.color } : {}}
              data-testid={`mood-pill-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Track List */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="track-list">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Sparkles size={14} className="text-violet-500" /> Curated Tracks
            </h3>
            <span className="text-xs text-gray-400">{activePlaylist.tracks.length} songs</span>
          </div>
          <div className="divide-y divide-gray-50">
            {activePlaylist.tracks.map((track, i) => {
              const links = buildStreamingLinks(track);
              return (
                <div key={i} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-gray-50 transition-all group" data-testid={`track-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-violet-100 transition-all"
                    style={{ backgroundColor: `${activePlaylist.color}15` }}>
                    <Play size={16} style={{ color: activePlaylist.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{track.title}</p>
                    <p className="text-xs text-gray-500 truncate">{track.artist}</p>
                  </div>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-medium hidden md:block">{track.genre}</span>
                  <div className="flex items-center gap-1.5" data-testid={`track-${i}-streaming-links`}>
                    <a
                      href={links.spotify}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open "${track.title}" on Spotify`}
                      aria-label={`Open ${track.title} by ${track.artist} on Spotify`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-extrabold hover:scale-110 hover:shadow-md transition-transform"
                      style={{ background: '#1DB954' }}
                      data-testid={`track-${i}-spotify-link`}
                    >
                      S
                    </a>
                    <a
                      href={links.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open "${track.title}" on YouTube`}
                      aria-label={`Open ${track.title} by ${track.artist} on YouTube`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:scale-110 hover:shadow-md transition-transform"
                      style={{ background: '#FF0000' }}
                      data-testid={`track-${i}-youtube-link`}
                    >
                      <Youtube size={14} />
                    </a>
                    <a
                      href={links.apple}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open "${track.title}" on Apple Music`}
                      aria-label={`Open ${track.title} by ${track.artist} on Apple Music`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-extrabold hover:scale-110 hover:shadow-md transition-transform"
                      style={{ background: 'linear-gradient(135deg, #FA233B 0%, #FB5C74 100%)' }}
                      data-testid={`track-${i}-apple-link`}
                    >
                      A
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mood History */}
        {data.mood_history.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5" data-testid="mood-history">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Recent Mood Trend</h3>
            <div className="flex items-end gap-1 h-16">
              {data.mood_history.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t transition-all"
                    style={{
                      height: `${(m.mood / 5) * 100}%`,
                      backgroundColor: m.mood >= 4 ? '#10B981' : m.mood >= 3 ? '#3B82F6' : m.mood >= 2 ? '#F59E0B' : '#EF4444',
                      minHeight: '4px',
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[9px] text-gray-400 mt-1">
              <span>14 days ago</span>
              <span>Today</span>
            </div>
          </div>
        )}

        {!data.has_mood_data && (
          <div className="mt-6 p-4 bg-violet-50 border border-violet-200 rounded-xl text-center" data-testid="no-mood-notice">
            <p className="text-sm text-violet-700">Log your mood on the dashboard to get personalized playlists!</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MoodPlaylist;
